import * as THREE from 'three';
import { mat, acquire, aimPointOf, clearLine, fireRound, tracers, inFrontOfPlayer, clamp, registerStreak, unregisterStreak } from './common.js';

/**
 * Sentry Turret — area denial you place and walk away from.
 *
 * Drops on the deck three metres ahead of the player, then engages anything that
 * enters a forward cone and has line of sight. It fires in bursts rather than
 * continuously so it reads as a weapon rather than a hose, and it leads nothing —
 * a sentry that never misses stops the player from having to hold the angle
 * themselves, which is the opposite of what area denial is for.
 *
 * Damage goes through the standard contract in `common.fireRound`, so the sentry's
 * kills feed the killfeed, the score, and the player's own streak progress exactly
 * like a rifle round would.
 */

export const TUNING = {
  lifetime: 62,
  placeDistance: 3.2,

  range: 42,
  coneHalfAngle: 1.22, // rad, ~140 degrees total
  damage: 21,

  burst: 4,
  burstGap: 0.085, // s between rounds inside a burst
  burstCooldown: 0.62, // s between bursts
  ammo: 96,

  turnRate: 3.4, // rad/s of yaw slew
  pitchRate: 2.6,
  acquireInterval: 0.22, // s between target searches
  spinUp: 0.9, // s of deploy animation before it can shoot

  muzzleHeight: 0.95,
};

const _target = new THREE.Vector3();
const _muzzle = new THREE.Vector3();
const _dir = new THREE.Vector3();
const _fwd = new THREE.Vector3();

export class SentryTurret {
  static id = 'sentry';

  constructor(ctx, sys) {
    this.ctx = ctx;
    this.sys = sys;
    this.root = null;
    this._scratch = [];
    registerStreak(ctx, this, 'SENTRY');
  }

  _build() {
    if (this.root) return;
    const g = new THREE.Group();
    const hull = mat('hull');
    const dark = mat('hullDark');

    // Tripod: three splayed legs plus a hub.
    for (let i = 0; i < 3; i++) {
      const leg = new THREE.Mesh(new THREE.CylinderGeometry(0.045, 0.06, 0.82, 6), dark);
      const a = (i / 3) * Math.PI * 2;
      leg.position.set(Math.cos(a) * 0.2, 0.36, Math.sin(a) * 0.2);
      leg.rotation.set(Math.cos(a) * 0.34, 0, -Math.sin(a) * 0.34);
      g.add(leg);
    }
    const hub = new THREE.Mesh(new THREE.CylinderGeometry(0.19, 0.24, 0.16, 10), hull);
    hub.position.y = 0.74;
    g.add(hub);

    // Yaw ring — everything above it rotates.
    const yaw = new THREE.Group();
    yaw.position.y = 0.82;
    g.add(yaw);

    const body = new THREE.Mesh(new THREE.BoxGeometry(0.36, 0.24, 0.34), hull);
    body.position.y = 0.06;
    yaw.add(body);

    const eye = new THREE.Mesh(new THREE.SphereGeometry(0.07, 8, 6), mat('hostile'));
    eye.position.set(0, 0.16, 0.14);
    yaw.add(eye);
    this.eye = eye;

    // Pitch cradle.
    const pitch = new THREE.Group();
    pitch.position.set(0, 0.09, 0);
    yaw.add(pitch);

    const barrel = new THREE.Mesh(new THREE.CylinderGeometry(0.045, 0.05, 0.72, 8), dark);
    barrel.rotation.x = Math.PI / 2;
    barrel.position.z = 0.36;
    pitch.add(barrel);

    const shroud = new THREE.Mesh(new THREE.BoxGeometry(0.2, 0.16, 0.26), hull);
    shroud.position.z = 0.06;
    pitch.add(shroud);

    const mag = new THREE.Mesh(new THREE.BoxGeometry(0.14, 0.26, 0.2), mat('olive'));
    mag.position.set(0, 0.2, -0.06);
    pitch.add(mag);

    const flash = new THREE.Mesh(new THREE.SphereGeometry(0.11, 6, 5), mat('accent'));
    flash.position.z = 0.74;
    flash.visible = false;
    pitch.add(flash);
    this.flash = flash;

    this.yawNode = yaw;
    this.pitchNode = pitch;
    g.name = 'streak-sentry';
    g.visible = false;
    this.ctx.scene.add(g);
    this.root = g;

    this.light = new THREE.PointLight(0xffb45a, 0, 9, 2);
    this.light.position.set(0, 1.05, 0);
    g.add(this.light);
  }

  deploy() {
    this._build();
    const p = inFrontOfPlayer(this.ctx, TUNING.placeDistance, _target);
    this.root.position.copy(p);
    this.root.rotation.y = 0;
    this.root.visible = true;

    this.t = 0;
    this.ammoLeft = TUNING.ammo;
    this.yaw = (this.ctx.player?.yaw ?? 0) + Math.PI;
    this.pitch = 0;
    this.target = null;
    this.acquireTimer = 0;
    this.burstLeft = 0;
    this.shotTimer = 0;
    this.cooldown = TUNING.spinUp;
    this.flashTimer = 0;

    this.ctx.events.emit('audio:play', { id: 'reload_mag_in', volume: 0.9, pitch: 0.7 });
    this.ctx.events.emit('audio:play', { id: 'bolt_release', volume: 0.8, pitch: 0.75, delay: 0.35 });
    return true;
  }

  update(dt) {
    this.t += dt;
    if (this.t >= TUNING.lifetime || this.ammoLeft <= 0) {
      this.end();
      return false;
    }

    const base = this.root.position;
    _muzzle.set(base.x, base.y + TUNING.muzzleHeight, base.z);

    /* ---- acquisition ---- */
    this.acquireTimer -= dt;
    if (this.target && (this.target.dead || !clearLine(this.ctx, _muzzle, aimPointOf(this.target, _target)))) {
      this.target = null;
    }
    if (!this.target && this.acquireTimer <= 0) {
      this.acquireTimer = TUNING.acquireInterval;
      _fwd.set(-Math.sin(this.yaw), 0, -Math.cos(this.yaw));
      this.target = acquire(this.ctx, _muzzle, TUNING.range, _fwd, Math.cos(TUNING.coneHalfAngle), this._scratch, this);
    }

    /* ---- slew ---- */
    if (this.target) {
      aimPointOf(this.target, _target);
      _dir.subVectors(_target, _muzzle);
      const wantYaw = Math.atan2(-_dir.x, -_dir.z);
      const wantPitch = Math.atan2(_dir.y, Math.hypot(_dir.x, _dir.z));
      let dy = wantYaw - this.yaw;
      dy = ((dy + Math.PI) % (Math.PI * 2) + Math.PI * 2) % (Math.PI * 2) - Math.PI;
      this.yaw += clamp(dy, -TUNING.turnRate * dt, TUNING.turnRate * dt);
      const dp = wantPitch - this.pitch;
      this.pitch += clamp(dp, -TUNING.pitchRate * dt, TUNING.pitchRate * dt);
      this._onTarget = Math.abs(dy) < 0.09 && Math.abs(dp) < 0.09;
    } else {
      // Idle sweep, so a dormant sentry still looks like it is doing something.
      this.yaw += Math.sin(this.t * 0.7) * dt * 0.55;
      this.pitch += (0 - this.pitch) * Math.min(1, dt * 3);
      this._onTarget = false;
    }
    // `this.yaw` is in the *player* convention (forward = -sin, -cos), which is what
    // `_shoot` builds its ray from. A mesh with `rotation.y = yaw` points its local
    // +Z at (+sin, +cos) — the exact opposite. Without the half turn the rounds go
    // one way and the barrel points the other, which is precisely what the first
    // deployment looked like: muzzle flash out of the back of the gun.
    this.yawNode.rotation.y = this.yaw + Math.PI;
    this.pitchNode.rotation.x = -this.pitch;

    /* ---- fire control ---- */
    this.cooldown -= dt;
    this.shotTimer -= dt;
    if (this.target && this._onTarget) {
      if (this.burstLeft > 0) {
        if (this.shotTimer <= 0) {
          this._shoot(_muzzle);
          this.shotTimer = TUNING.burstGap;
          if (--this.burstLeft === 0) this.cooldown = TUNING.burstCooldown;
        }
      } else if (this.cooldown <= 0) {
        this.burstLeft = Math.min(TUNING.burst, this.ammoLeft);
        this.shotTimer = 0;
      }
    }

    /* ---- muzzle flash decay ---- */
    if (this.flashTimer > 0) {
      this.flashTimer -= dt;
      if (this.flashTimer <= 0) {
        this.flash.visible = false;
        this.light.intensity = 0;
      } else {
        this.light.intensity = this.flashTimer * 90;
      }
    }
    this.eye.material = this.target ? mat('hostile') : mat('accentDim');
    return true;
  }

  _shoot(muzzle) {
    const yawN = this.yaw;
    const p = this.pitch;
    // Fire from the barrel tip along the turret's actual pointing vector, with a
    // touch of dispersion so a long burst is not a laser.
    const spread = 0.012;
    const jx = (Math.random() - 0.5) * spread;
    const jy = (Math.random() - 0.5) * spread;
    _dir.set(
      -Math.sin(yawN + jx) * Math.cos(p + jy),
      Math.sin(p + jy),
      -Math.cos(yawN + jx) * Math.cos(p + jy)
    ).normalize();

    const start = _target.copy(muzzle).addScaledVector(_dir, 0.75);
    const hit = fireRound(this.ctx, start, _dir, TUNING.range + 12, TUNING.damage, { source: this, weapon: 'sentry' });
    const end = hit?.point ?? start.clone().addScaledVector(_dir, TUNING.range);
    tracers(this.ctx).fire(start, end);

    this.ammoLeft--;
    this.flash.visible = true;
    this.flashTimer = 0.045;
    this.light.intensity = 4;
    this.ctx.events.emit('audio:play', {
      id: 'enemy_fire',
      position: { x: muzzle.x, y: muzzle.y, z: muzzle.z },
      volume: 0.62,
      pitch: 1.28,
    });
  }

  end() {
    if (!this.root) return;
    this.root.visible = false;
    this.light.intensity = 0;
    this.flash.visible = false;
    this.ctx.events.emit('audio:play', { id: 'dryfire', volume: 0.7, pitch: 0.65 });
  }

  /** What the HUD shows while this is running. */
  status() {
    return `${this.ammoLeft}`;
  }

  dispose() {
    unregisterStreak(this.ctx, this);
    if (!this.root) return;
    this.root.parent?.remove(this.root);
    this.root.traverse((o) => o.geometry?.dispose?.());
    this.root = null;
  }
}
